import { BrandSettings, deterministicId, newId, type Signoff } from '@quickreport/domain';
import { type SignatoryRow as PrintedSignatory, readBrand, signoffFrom } from '@quickreport/pdf';
import { queueChange } from '@quickreport/sync';
import type { LocalDb } from './client';

export async function orgBrandId(orgId: string): Promise<string> {
  return deterministicId(`brand:${orgId}`);
}

/** The saved settings, or sensible starting values for a lab that has not saved any yet. */
export async function loadBrand(
  db: LocalDb,
  orgId: string,
  defaults: Partial<BrandSettings> = {},
): Promise<BrandSettings> {
  const r = await db.query<{ doc: unknown }>(
    'select doc from brand_settings where org_id = $1 and site_id is null and deleted_at is null limit 1',
    [orgId],
  );
  // The same reading the Android app does before it prints.
  return readBrand(r.rows[0]?.doc, defaults);
}

export async function brandExists(db: LocalDb, orgId: string): Promise<boolean> {
  const r = await db.query(
    'select 1 from brand_settings where org_id = $1 and site_id is null and deleted_at is null',
    [orgId],
  );
  return r.rows.length > 0;
}

/** One brand row per organization, with an id every device derives the same way. */
export async function saveBrand(db: LocalDb, orgId: string, doc: BrandSettings): Promise<void> {
  const id = await orgBrandId(orgId);
  const clean = BrandSettings.parse(doc);
  await db.transaction(async (tx) => {
    const have = await tx.query('select 1 from brand_settings where id = $1', [id]);
    if (have.rows[0]) {
      await tx.query(
        'update brand_settings set doc = $2::jsonb, updated_at = now(), deleted_at = null where id = $1',
        [id, JSON.stringify(clean)],
      );
    } else {
      await tx.query(
        'insert into brand_settings (id, org_id, site_id, doc) values ($1, $2, null, $3::jsonb)',
        [id, orgId, JSON.stringify(clean)],
      );
    }
    await queueChange(tx, 'brand_settings', id);
  });
}

export interface SignatoryRow {
  id: string;
  name: string;
  qualification: string;
  designation: string | null;
  reg_no: string | null;
  signature_file_id: string | null;
  departments: string[];
  member_id: string | null;
}

export async function saveSignatory(
  db: LocalDb,
  orgId: string,
  s: Omit<SignatoryRow, 'id'> & { id?: string },
): Promise<string> {
  const id = s.id ?? newId();
  await db.transaction(async (tx) => {
    const have = await tx.query('select 1 from signatories where id = $1', [id]);
    const args = [
      id,
      s.name.trim(),
      s.qualification.trim(),
      s.designation || null,
      s.reg_no || null,
      s.signature_file_id,
      s.departments,
      s.member_id,
    ];
    if (have.rows[0]) {
      await tx.query(
        `update signatories set name = $2, qualification = $3, designation = $4, reg_no = $5,
           signature_file_id = $6, departments = $7, member_id = $8, updated_at = now() where id = $1`,
        args,
      );
    } else {
      await tx.query(
        `insert into signatories (id, org_id, name, qualification, designation, reg_no, signature_file_id, departments, member_id)
         values ($1, $9, $2, $3, $4, $5, $6, $7, $8)`,
        [...args, orgId],
      );
    }
    await queueChange(tx, 'signatories', id);
  });
  return id;
}

export async function removeSignatory(db: LocalDb, id: string): Promise<void> {
  await db.transaction(async (tx) => {
    await tx.query('update signatories set deleted_at = now(), updated_at = now() where id = $1', [
      id,
    ]);
    await queueChange(tx, 'signatories', id);
  });
}

/** The lab's branding and the signing doctor as they are right now, to keep with a signed report. */
export async function buildSignoff(
  db: LocalDb,
  orgId: string,
  orgName: string,
  signatoryId: string | null,
): Promise<Signoff> {
  const brand = await loadBrand(db, orgId, { labName: orgName });
  const r = await db.query<PrintedSignatory>(
    'select id, name, qualification, designation, reg_no, signature_file_id from signatories where id = $1',
    // No signing doctor (a lab that prints no signature): nothing matches, the list is empty.
    [signatoryId ?? '00000000-0000-0000-0000-000000000000'],
  );
  // The Android app builds the same snapshot through the same function.
  return signoffFrom(brand, r.rows);
}
